import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import {
  SITE_CONTENT_DEFAULTS,
  mergeContent,
  type SiteContent,
  type SiteContentKey,
} from '../lib/siteContent';

/**
 * Contenuti del sito (tabella site_content) letti una volta sola per tutta
 * l'app: home, footer e pagina contatti condividono la stessa richiesta.
 * Dopo un salvataggio dalla dashboard la cache si aggiorna e ogni pagina
 * aperta mostra subito il nuovo testo.
 */

type Rows = Partial<Record<SiteContentKey, unknown>>;

let rows: Rows = {};
let loaded = false;
let request: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

function load() {
  // Con una rete lentissima non si lascia la home vuota: dopo un po' si
  // mostrano i testi predefiniti e quelli salvati arrivano quando arrivano.
  if (!request) {
    setTimeout(() => {
      if (!loaded) {
        loaded = true;
        emit();
      }
    }, 2500);
  }
  request ??= (async () => {
    const { data, error } = await supabase.from('site_content').select('key, content');
    // Se la lettura fallisce restano i testi predefiniti: meglio una home
    // con i testi di partenza che una home vuota.
    if (error) console.error('Error reading site content:', error);
    rows = Object.fromEntries((data ?? []).map((r) => [r.key, r.content]));
    loaded = true;
    emit();
  })();
  return request;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Il riferimento a `rows` cambia solo quando cambiano i dati, così
// useSyncExternalStore non fa render inutili.
let snapshot = { rows, loaded };
const getSnapshot = () => {
  if (snapshot.rows !== rows || snapshot.loaded !== loaded) snapshot = { rows, loaded };
  return snapshot;
};

/**
 * Contenuto di una sezione, già unito ai default. `loaded` resta false
 * finché il DB non ha risposto: la home lo usa per non mostrare per un
 * attimo il testo predefinito al posto di quello salvato.
 */
export function useSiteContent<K extends SiteContentKey>(key: K): { content: SiteContent[K]; loaded: boolean } {
  const state = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    load();
  }, []);

  return { content: mergeContent(SITE_CONTENT_DEFAULTS[key], state.rows[key]), loaded: state.loaded };
}

/** Solo admin con 2FA: la RLS rifiuta il salvataggio a chiunque altro. */
export async function saveSiteContent<K extends SiteContentKey>(key: K, content: SiteContent[K]) {
  const { data, error } = await supabase
    .from('site_content')
    .upsert({ key, content, updated_at: new Date().toISOString() })
    .select('key');
  if (error) throw new Error(error.message);
  // Con RLS un upsert non autorizzato può non toccare nessuna riga senza errore.
  if (!data || data.length === 0) throw new Error('Non autorizzato a modificare i contenuti');
  rows = { ...rows, [key]: content };
  emit();
}

/** Torna ai testi predefiniti cancellando la riga salvata. */
export async function resetSiteContent(key: SiteContentKey) {
  const { error } = await supabase.from('site_content').delete().eq('key', key);
  if (error) throw new Error(error.message);
  const next = { ...rows };
  delete next[key];
  rows = next;
  emit();
}

/** Contenuto salvato così com'è (senza default), per il registro modifiche. */
export const savedSiteContent = (key: SiteContentKey) => rows[key] ?? null;
